import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import PublicLegalLayout from "../../components/layout/PublicLegalLayout";

interface PolicySection {
  id: string;
  title: string;
  paragraphs: string[];
  list?: string[];
}

const LAST_UPDATED = "May 28, 2026";
const CONTACT_EMAIL = "privacy@aetherflowsystems.com";

const sections: PolicySection[] = [
  {
    id: "introduction",
    title: "1. Introduction",
    paragraphs: [
      'AetherFlow Systems ("AetherFlow," "we," "us," or "our") operates a project management and collaboration platform that connects clients, developers, and administrators. This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our website, applications, and related services (collectively, the "Service").',
      "By creating an account or using the Service, you agree to the practices described in this policy. If you do not agree, please do not use the Service.",
    ],
  },
  {
    id: "information-we-collect",
    title: "2. Information We Collect",
    paragraphs: ["We collect information in the following categories:"],
    list: [
      "Account information: name, email address, password (stored in hashed form), role (client, developer, or admin), and profile details you provide.",
      "Professional information (developers): skills, education, certifications, portfolio documents, resumes, and approval status.",
      "Project and task data: project descriptions, milestones, tasks, messages, files, deadlines, and progress updates you submit through the platform.",
      "Payment information: billing details and transaction records processed through our payment partners. We do not store full card numbers on our servers.",
      "Communications: messages sent via in-app chat, contact form submissions, and support correspondence.",
      "Technical data: IP address, browser type, device information, log data, and usage information related to how you interact with the Service.",
      "Authentication data: one-time passwords (OTPs) and session tokens used to secure your account.",
    ],
  },
  {
    id: "how-we-use",
    title: "3. How We Use Your Information",
    paragraphs: ["We use personal information to:"],
    list: [
      "Provide, operate, and maintain the Service.",
      "Authenticate users and secure accounts, including sending OTP and verification emails.",
      "Facilitate project collaboration between clients and developers.",
      "Process payments, payouts, and related financial records.",
      "Send service-related notifications, updates, and administrative messages.",
      "Review developer applications and manage platform administration.",
      "Improve performance, troubleshoot issues, and protect against fraud or abuse.",
      "Comply with legal obligations and enforce our terms and policies.",
    ],
  },
  {
    id: "legal-basis",
    title: "4. Legal Basis for Processing",
    paragraphs: [
      "Where applicable law requires a legal basis, we process your data based on: (a) performance of a contract with you when you use the Service; (b) your consent, such as when you opt in to communications; (c) our legitimate interests in operating and securing the platform; and (d) compliance with legal obligations.",
    ],
  },
  {
    id: "sharing",
    title: "5. How We Share Information",
    paragraphs: [
      "We do not sell your personal information. We may share information in these circumstances:",
    ],
    list: [
      "With other users as needed to deliver the Service (for example, clients and developers collaborating on a project).",
      "With service providers who assist us, such as cloud hosting, email delivery, file storage, payment processors, and analytics tools, under contractual confidentiality obligations.",
      "When required by law, regulation, legal process, or governmental request.",
      "To protect the rights, safety, and security of AetherFlow, our users, or the public.",
      "In connection with a merger, acquisition, or sale of assets, with notice where required by law.",
    ],
  },
  {
    id: "third-party",
    title: "6. Third-Party Services",
    paragraphs: [
      "The Service may integrate with third-party providers including payment processors (such as Paystack), cloud storage (such as Cloudinary), email providers, and optional embedded tools (such as chat widgets). These providers process data according to their own privacy policies. We encourage you to review those policies when you interact with integrated features.",
    ],
  },
  {
    id: "retention",
    title: "7. Data Retention",
    paragraphs: [
      "We retain personal information for as long as your account is active or as needed to provide the Service, resolve disputes, enforce agreements, and meet legal requirements. When data is no longer required, we delete or anonymize it using reasonable technical measures.",
    ],
  },
  {
    id: "security",
    title: "8. Security",
    paragraphs: [
      "We implement administrative, technical, and organizational safeguards designed to protect your information, including encrypted connections (HTTPS), hashed passwords, access controls, and secure authentication practices. No method of transmission or storage is completely secure; we cannot guarantee absolute security.",
    ],
  },
  {
    id: "your-rights",
    title: "9. Your Rights and Choices",
    paragraphs: [
      "Depending on your location, you may have the right to:",
      "To exercise these rights, contact us at the email below. We may need to verify your identity before fulfilling a request.",
    ],
    list: [
      "Access, correct, or update your personal information through your account settings.",
      "Request deletion of your account and associated data, subject to legal retention requirements.",
      "Object to or restrict certain processing activities.",
      "Withdraw consent where processing is consent-based.",
      "Request a copy of your data in a portable format where applicable.",
    ],
  },
  {
    id: "cookies",
    title: "10. Cookies and Local Storage",
    paragraphs: [
      "We use cookies and similar technologies, including browser local storage, to maintain your session, remember preferences (such as theme settings), and keep you signed in. You can control cookies through your browser settings, but disabling them may affect Service functionality.",
    ],
  },
  {
    id: "children",
    title: "11. Children's Privacy",
    paragraphs: [
      "The Service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe a child has provided us data, please contact us and we will take appropriate steps to delete it.",
    ],
  },
  {
    id: "international",
    title: "12. International Data Transfers",
    paragraphs: [
      "Your information may be processed in countries other than your own, including where our servers or service providers operate. We take steps to ensure appropriate safeguards are in place when transferring data across borders, consistent with applicable law.",
    ],
  },
  {
    id: "changes",
    title: "13. Changes to This Policy",
    paragraphs: [
      'We may update this Privacy Policy from time to time. We will post the revised version on this page and update the "Last updated" date. Material changes may be communicated via email or in-app notice. Continued use of the Service after changes become effective constitutes acceptance of the updated policy.',
    ],
  },
  {
    id: "contact",
    title: "14. Contact Us",
    paragraphs: [
      `If you have questions about this Privacy Policy or our data practices, contact us at ${CONTACT_EMAIL}.`,
    ],
  },
];

const PrivacyPolicy: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const sectionClass = `space-y-8 ${isDark ? "text-gray-300" : "text-gray-700"}`;
  const headingClass = `text-xl font-semibold mb-3 scroll-mt-24 ${
    isDark ? "text-white" : "text-gray-900"
  }`;
  const paragraphClass = "leading-relaxed";
  const listClass = `list-disc pl-6 space-y-2 mt-3 ${isDark ? "text-gray-400" : "text-gray-600"}`;

  return (
    <PublicLegalLayout title="Privacy Policy" lastUpdated={LAST_UPDATED}>
      <nav
        className={`mb-10 pb-8 border-b ${
          isDark ? "border-gray-700" : "border-gray-200"
        }`}
        aria-label="Table of contents"
      >
        <p
          className={`text-sm font-semibold uppercase tracking-wider mb-4 ${
            isDark ? "text-gray-400" : "text-gray-500"
          }`}
        >
          On this page
        </p>
        <ul className="grid sm:grid-cols-2 gap-2 text-sm">
          {sections.map((section) => (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                className={`hover:underline ${
                  isDark ? "text-cyan-400" : "text-blue-600"
                }`}
              >
                {section.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className={sectionClass}>
        {sections.map((section) => {
          const renderParagraph = (paragraph: string, index: number) => (
            <p key={index} className={`${paragraphClass} ${index > 0 ? "mt-3" : ""}`}>
              {paragraph.includes(CONTACT_EMAIL) ? (
                <>
                  {paragraph.split(CONTACT_EMAIL)[0]}
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className={`font-medium underline ${
                      isDark ? "text-cyan-400" : "text-blue-600"
                    }`}
                  >
                    {CONTACT_EMAIL}
                  </a>
                  {paragraph.split(CONTACT_EMAIL)[1]}
                </>
              ) : (
                paragraph
              )}
            </p>
          );

          const [intro, ...rest] = section.paragraphs;

          return (
            <section key={section.id} id={section.id}>
              <h2 className={headingClass}>{section.title}</h2>
              {intro && renderParagraph(intro, 0)}
              {section.list && (
                <ul className={listClass}>
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
              {rest.map((paragraph, index) => renderParagraph(paragraph, index + 1))}
            </section>
          );
        })}
      </div>

      <div
        className={`mt-10 pt-8 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${
          isDark ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          Questions? Email{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className={`font-medium ${isDark ? "text-cyan-400" : "text-blue-600"}`}
          >
            {CONTACT_EMAIL}
          </a>
        </p>
        <Link
          to="/"
          className={`inline-flex justify-center items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            isDark
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          Back to homepage
        </Link>
      </div>
    </PublicLegalLayout>
  );
};

export default PrivacyPolicy;
